import {useQuery} from "@tanstack/react-query";
import {AxiosError} from "axios";
import {createProductServiceRepository} from "../data/repositories";
import {ProductServiceCustomerPurchase} from "../domain/product-service.types";
import {PaginationMeta} from "../../../redux/service/i-service";
import {
  GET_STORE_SERVICES_SALES_AND_INSIGHTS_RQ_KEY,
  GET_STORE_SERVICES_SALES_AND_INSIGHTS_CUSTOMER_TRANSACTIONS_RQ_KEY,
} from "../../../react-query-hooks/store/services/constants";
import {
  GetStoreServiceSalesAndInsightsApiResponse,
  GetStoreServiceSalesAndInsightCustomerTransactionsApiResponse,
} from "../../../react-query-hooks/store/services/get-store-service-sales-and-insights/types";

type LegacyPurchase =
  GetStoreServiceSalesAndInsightCustomerTransactionsApiResponse["customerPurchases"][number];

/**
 * Maps a canonical customer purchase to the legacy (snake_case) shape. The
 * customers table reads only name/email/reference/date/amount/currency and
 * booking_questions; the rest are defaulted.
 */
const toLegacyPurchase = (p: ProductServiceCustomerPurchase): LegacyPurchase => ({
  id: p.id,
  userId: p.userId,
  productServiceId: p.productServiceId,
  firstName: p.firstName,
  lastName: p.lastName,
  emailAddress: p.emailAddress,
  currency: p.currency,
  amount: p.amount === 0 ? "0.00" : String(p.amount),
  charges: String(p.charges),
  reference: p.reference,
  processor_reference: null,
  statusId: p.statusId,
  is_paid: p.isPaid,
  payment_channel: p.paymentChannel ?? "",
  discount_code: p.discountCode ?? null,
  is_discounted: false,
  charge_bearer: (p.chargeBearer as "customer" | "coach") ?? "customer",
  booking_questions: p.bookingQuestions,
  delivery_date: p.deliveryDate ?? "",
  revision_requested: false,
  createdAt: p.createdAt,
  updatedAt: p.updatedAt,
});

/**
 * Legacy-compatible sales-insights hook. Routes through the feature repository
 * (always v2); preserves the response shape and query key.
 *
 * @deprecated Use useProductServiceSalesInsights from features/product-services.
 */
export const useGetStoreServiceSalesAndInsightsApi = (storeServiceId?: number) => {
  const repository = createProductServiceRepository();

  return useQuery<
    GetStoreServiceSalesAndInsightsApiResponse,
    AxiosError<{status: boolean; message: string}>
  >({
    queryKey: [GET_STORE_SERVICES_SALES_AND_INSIGHTS_RQ_KEY, String(storeServiceId)],
    queryFn: async () => {
      const insights = await repository.getSalesInsights(storeServiceId!);
      return {
        totalRevenue: insights.totalRevenue,
        totalPurchases: insights.totalPurchases,
      };
    },
    enabled: !!storeServiceId,
  });
};

/**
 * Legacy-compatible customer-transactions hook. Routes through the feature
 * repository (always v2) and rebuilds the legacy `{data:{customerPurchases}, meta}`.
 *
 * NOTE: preserves the original query key, which omits `page` — pagination is
 * latently broken here (clicking next won't refetch). The domain hook
 * (`useProductServiceCustomerPurchases`) keys by page and paginates correctly.
 *
 * @deprecated Use useProductServiceCustomerPurchases from features/product-services.
 */
export const useGetStoreServiceSalesAndInsightsCustomerTransactionsApi = ({
  currentPage,
  storeServiceId,
}: {
  storeServiceId?: number;
  currentPage: number;
}) => {
  const repository = createProductServiceRepository();

  return useQuery<
    {
      data: GetStoreServiceSalesAndInsightCustomerTransactionsApiResponse;
      meta: PaginationMeta;
    },
    AxiosError<{status: boolean; message: string}>
  >({
    queryKey: [
      GET_STORE_SERVICES_SALES_AND_INSIGHTS_RQ_KEY,
      GET_STORE_SERVICES_SALES_AND_INSIGHTS_CUSTOMER_TRANSACTIONS_RQ_KEY,
      String(storeServiceId),
    ],
    queryFn: async () => {
      const result = await repository.getCustomerPurchases({
        productServiceId: storeServiceId!,
        page: currentPage,
        perPage: 5,
      });
      const customerPurchases = result.data.map(toLegacyPurchase);

      if ("meta" in result) {
        const {currentPage: cp, perPage, lastPage} = result.meta;
        return {
          data: {customerPurchases},
          meta: {
            current_page: cp,
            next_page: cp < lastPage ? cp + 1 : 0,
            per_page: perPage,
            previous_page: cp > 1 ? cp - 1 : 0,
            total_pages: lastPage,
          },
        };
      }

      return {
        data: {customerPurchases},
        meta: {
          current_page: currentPage,
          next_page: result.hasNextPage ? currentPage + 1 : 0,
          per_page: 5,
          previous_page: currentPage > 1 ? currentPage - 1 : 0,
          total_pages: Math.ceil(result.total / 5),
        },
      };
    },
    enabled: !!storeServiceId,
  });
};
